import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { User, UserRole } from "../../../types";
import apiService from "../../../services/api";

import {
  validateBusinessEmail,
  validatePassword,
  validateName,
  validateConfirmPassword,
} from "../../../utils/validation";
import ConfirmationModal from "../../../components/ConfirmationModal";
import Toast from "../../../components/Toast";
import Alert from "../../../components/Alert";
import SelectdDropDown from "../../../components/DropDownComponent";
import { Users } from "lucide-react";
import DefaultModal from "../CompanySettings/JobApproval/component/dialog";
import { useUserManagementViewModel } from "./viewModel/userManagement.viewmodel";
import { HirearchyView } from "./view/hierarchyView";

interface NewUser {
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  job_title: string;
  reports_to_id?: string;
  send_email: boolean;
}

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  type: "danger" | "warning" | "info";
  loading: boolean;
}

interface ToastState {
  isVisible: boolean;
  message: string;
  type: "success" | "error" | "warning" | "info";
}

const UserManagement: React.FC = () => {
  const { state, dispatch } = useUserManagementViewModel();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState<
    "active" | "inactive" | "pending" | "all"
  >("all");
  const filteredUsers = Object.values(users).filter((user) =>
    user.full_name.toLowerCase().includes(search.toLowerCase()),
  );
  const [newUser, setNewUser] = useState<NewUser>({
    email: "",
    first_name: "",
    last_name: "",
    role: UserRole.RECRUITER,
    job_title: "",
    send_email: true,
  });

  // Confirmation modal state
  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
    loading: false,
  });

  // Toast notification state
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    message: "",
    type: "success",
  });

  const showToast = (
    message: string,
    type: "success" | "error" | "warning" | "info",
  ) => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const showConfirmation = (
    title: string,
    message: string,
    onConfirm: () => void,
    type: "danger" | "warning" | "info" = "info",
  ) => {
    setConfirmation({
      isOpen: true,
      title,
      message,
      onConfirm,
      type,
      loading: false,
    });
  };
  const openEditModal = (user: User) => {
    const matchedManager = users.find(
      (u) =>
        u.id === user.reports_to_id || u.full_name === user.reports_to_name,
    );

    setEditingUser({
      ...user,
      reports_to_id: matchedManager?.id || user.reports_to_id || "",
      reports_to_name: matchedManager?.full_name || user.reports_to_name || "",
    });

    setOpen(false);
    setSearch("");
  };

  const hideConfirmation = () => {
    setConfirmation((prev) => ({ ...prev, isOpen: false }));
  };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      if (currentUser?.company_id) {
        const data = await apiService.getCompanyUsers(currentUser.company_id, {
          status_filter: statusFilter,
        });
        setUsers(data);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load users");
      showToast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.company_id, statusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const validateNewUserForm = (): boolean => {
    const errors: Record<string, string> = {};

    const firstNameValidation = validateName(newUser.first_name, "First name");
    if (!firstNameValidation.isValid)
      errors.first_name = firstNameValidation.message;

    const lastNameValidation = validateName(newUser.last_name, "Last name");
    if (!lastNameValidation.isValid)
      errors.last_name = lastNameValidation.message;

    const emailValidation = validateBusinessEmail(newUser.email);
    if (!emailValidation.isValid) errors.email = emailValidation.message;

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateNewUserForm()) {
      setError("Please fix the errors above");
      return;
    }

    try {
      setError(null);
      if (currentUser?.company_id) {
        // Remove empty keys from newUser
        const payload = Object.fromEntries(
          Object.entries(newUser).filter(([_, v]) =>
            typeof v === "boolean" ? true : v !== "",
          ),
        );
        await apiService.inviteUser(currentUser.company_id, payload);
        setShowAddModal(false);
        setFieldErrors({});
        loadUsers();
        showToast(
          `Invitation sent to ${newUser.first_name} ${newUser.last_name}`,
          "success",
        );
      }
    } catch (err: any) {
      setError(err.message || "Failed to create user");
      showToast("Failed to send invitation", "error");
    } finally {
      setNewUser({
        email: "",
        first_name: "",
        last_name: "",
        role: UserRole.RECRUITER,
        job_title: "",
        send_email: true,
      });
    }
  };

  const handleUpdateUser = async (userId: string, updates: Partial<User>) => {
    try {
      await apiService.updateUser(userId, updates);
      loadUsers();
      setEditingUser(null);
      showToast("User updated successfully", "success");
    } catch (err: any) {
      setError(err.message || "Failed to update user");
      showToast("Failed to update user", "error");
    }
  };


  const handleResendInvitation = async (userId: string, userEmail: string) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    showConfirmation(
      "Resend Invitation",
      `Resend invitation to ${user.first_name} ${user.last_name} (${userEmail})? This will generate a new temporary password.`,
      async () => {
        setConfirmation((prev) => ({ ...prev, loading: true }));
        try {
          if (currentUser?.company_id) {
            await apiService.resendInvitation(currentUser.company_id, userId);
            hideConfirmation();
            showToast(
              `Invitation resent to ${user.first_name} ${user.last_name}`,
              "success",
            );
          }
        } catch (err: any) {
          setError(err.message || "Failed to resend invitation");
          showToast("Failed to resend invitation", "error");
          setConfirmation((prev) => ({ ...prev, loading: false }));
        }
      },
      "warning",
    );
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    const action = isActive ? "deactivate" : "activate";
    const actionTitle = isActive ? "Deactivate User" : "Activate User";

    showConfirmation(
      actionTitle,
      `Are you sure you want to ${action} ${user.first_name} ${user.last_name}?`,
      async () => {
        setConfirmation((prev) => ({ ...prev, loading: true }));
        try {
          if (isActive) {
            // Deactivate user
            await apiService.deactivateUser(userId);
          } else {
            // Activate user
            await apiService.activateUser(userId);
          }
          loadUsers(); // Reload users to reflect changes
          hideConfirmation();
          showToast(
            `${user.first_name} ${user.last_name} has been ${action}d`,
            "success",
          );
        } catch (err: any) {
          setError(err.message || `Failed to ${action} user`);
          showToast(`Failed to ${action} user`, "error");
          setConfirmation((prev) => ({ ...prev, loading: false }));
        }
      },
      isActive ? "warning" : "info",
    );
  };

  const getRoleDisplayName = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return "Admin";
      case UserRole.RECRUITER:
        return "Recruiter";
      case UserRole.VIEWER:
        return "Viewer";
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4 mb-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-3 sm:px-4 lg:max-w-7xl lg:mx-auto">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-gray-600">
            Manage your team members and their access permissions.
          </p>
        </div>
        {state.activeTab === "User Management" && (
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Invite User
          </button>
        )}
      </div>
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6 overflow-x-auto">
            <button
              onClick={() =>
                dispatch({ type: "SET_ACTIVE_TAB", payload: "User Management" })
              }
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                state.activeTab === "User Management"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">💼</span>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <span>User Management</span>
                  </div>
                  <div className="text-xs text-gray-500 font-normal">
                    Create and manage team members
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() =>
                dispatch({
                  type: "SET_ACTIVE_TAB",
                  payload: "Team Hierarchy",
                })
              }
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                state.activeTab === "Team Hierarchy"
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">📋</span>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <span>Team Hierarchy</span>
                  </div>
                </div>
              </div>
            </button>
          </nav>
        </div>
      </div>
      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError(null)}
          className="mb-6"
        />
      )}

      {/* Users Table */}
      {state.activeTab === "User Management" && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Team Members</h3>

            {/* Status Filter */}
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-700">
                Filter:
              </label>
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "active" | "inactive" | "pending" | "all",
                  )
                }
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="active">Active Users</option>
                <option value="inactive">Inactive Users</option>
                <option value="pending">Pending Invitations</option>
                <option value="all">All Users</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-medium text-sm">
                              {user.first_name.charAt(0)}
                              {user.last_name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === UserRole.ADMIN
                            ? "bg-purple-100 text-purple-800"
                            : user.role === UserRole.RECRUITER
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {getRoleDisplayName(user.role)}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          !user.is_verified
                            ? "bg-yellow-100 text-yellow-800"
                            : user.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {!user.is_verified
                          ? "Pending Invitation"
                          : user.is_active
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login
                        ? new Date(user.last_login).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => openEditModal(user)}
                          className="inline-flex items-center justify-center w-8 h-8 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-full transition-colors"
                          title="Edit user"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() =>
                            handleToggleActive(user.id, user.is_active)
                          }
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                            user.is_active
                              ? "text-red-600 hover:text-red-900 hover:bg-red-50"
                              : "text-green-600 hover:text-green-900 hover:bg-green-50"
                          }`}
                          title={
                            user.is_active ? "Deactivate user" : "Activate user"
                          }
                        >
                          {user.is_active ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          )}
                        </button>
                        {!user.is_verified && (
                          <button
                            onClick={() =>
                              handleResendInvitation(user.id, user.email)
                            }
                            className="inline-flex items-center justify-center w-8 h-8 text-orange-600 hover:text-orange-900 hover:bg-orange-50 rounded-full transition-colors"
                            title="Resend invitation email with new temporary password"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {state.activeTab === "Team Hierarchy" && <HirearchyView></HirearchyView>}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full  z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Invite New User
              </h3>
              <form onSubmit={handleAddUser}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewUser({ ...newUser, email: value });
                      const validation = validateBusinessEmail(value);
                      setFieldErrors((prev) => ({
                        ...prev,
                        email: validation.isValid ? "" : validation.message,
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      fieldErrors.email
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {fieldErrors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newUser.first_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewUser({ ...newUser, first_name: value });
                      const validation = validateName(value, "First name");
                      setFieldErrors((prev) => ({
                        ...prev,
                        first_name: validation.isValid
                          ? ""
                          : validation.message,
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      fieldErrors.first_name
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {fieldErrors.first_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldErrors.first_name}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    required
                    value={newUser.last_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewUser({ ...newUser, last_name: value });
                      const validation = validateName(value, "Last name");
                      setFieldErrors((prev) => ({
                        ...prev,
                        last_name: validation.isValid ? "" : validation.message,
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      fieldErrors.last_name
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                  />
                  {fieldErrors.last_name && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldErrors.last_name}
                    </p>
                  )}
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        role: e.target.value as UserRole,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={UserRole.VIEWER}>Viewer</option>
                    <option value={UserRole.RECRUITER}>Recruiter</option>
                    <option value={UserRole.ADMIN}>Admin</option>
                  </select>
                </div>
                <div className="mb-6 relative w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report To
                  </label>

                  <div
                    className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer bg-white flex items-center justify-between"
                    onClick={() => setOpen(!open)}
                  >
                    <span className="truncate">
                      {users.find((u) => u.id === newUser.reports_to_id)
                        ?.full_name || "Select user"}
                    </span>
                    <span className="text-gray-400">⌄</span>
                  </div>

                  {open && (
                    <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 mb-4">
                      <input
                        type="text"
                        placeholder="Search user..."
                        className="w-full px-3 py-2 border-b outline-none"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />

                      <div className="max-h-40 overflow-y-auto">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <div
                              key={user.id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setNewUser({
                                  ...newUser,
                                  reports_to_id: user.id,
                                });
                                setOpen(false);
                                setSearch("");
                              }}
                            >
                              {user.full_name}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-400">
                            No results found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* <div className="mb-6 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report To
                  </label>

                  <div
                    className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer bg-white"
                    onClick={() => setOpen(!open)}
                  >
                    {users.find((u) => u.id === newUser.reports_to_id)
                      ?.full_name || "Select user"}
                  </div>

                  {open && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1">
                      <input
                        type="text"
                        placeholder="Search user..."
                        className="w-full px-3 py-2 border-b outline-none"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />

                      <div className="max-h-40 overflow-y-auto">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <div
                              key={user.id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setNewUser({
                                  ...newUser,
                                  reports_to_id: user.id,
                                });
                                setOpen(false);
                                setSearch("");
                              }}
                            >
                              {user.full_name}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-400">
                            No results found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div> */}

                {/* <SelectdDropDown
                  id=""
                  label="Report To"
                  required
                  placeholder="select users"
                  value={newUser.reports_to_id || ""}
                  options={users.map((u) => ({
                    label: u.full_name,
                    value: u.id,
                  }))}
                  onChange={(value) =>
                    setNewUser({ ...newUser, reports_to_id: value })
                  }
                  // error={jobState.validateErrors.hiring_manager}
                /> */}
                <div className="flex justify-end space-x-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit User
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateUser(editingUser.id, {
                    first_name: editingUser.first_name,
                    last_name: editingUser.last_name,
                    role: editingUser.role,
                    reports_to_id: editingUser.reports_to_id,
                  });
                }}
              >
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editingUser.email}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    value={editingUser.first_name}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        first_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    required
                    value={editingUser.last_name}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        last_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={editingUser.role}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        role: e.target.value as UserRole,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={UserRole.VIEWER}>Viewer</option>
                    <option value={UserRole.RECRUITER}>Recruiter</option>
                    <option value={UserRole.ADMIN}>Admin</option>
                  </select>
                </div>
                <div className="mb-6 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report To
                  </label>

                  <div
                    className="w-full px-3 py-2 border border-gray-300 rounded-md cursor-pointer bg-white"
                    onClick={() => setOpen(!open)}
                  >
                    {users.find((u) => u.id === editingUser.reports_to_id)
                      ?.full_name ||
                      editingUser.reports_to_name ||
                      "Select user"}
                  </div>

                  {open && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1">
                      <input
                        type="text"
                        placeholder="Search user..."
                        className="w-full px-3 py-2 border-b outline-none"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />

                      <div className="max-h-40 overflow-y-auto">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <div
                              key={user.id}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => {
                                setEditingUser({
                                  ...editingUser,
                                  reports_to_id: user.id,
                                  reports_to_name: user.full_name,
                                });
                                setOpen(false);
                                setSearch("");
                              }}
                            >
                              {user.full_name}
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-400">
                            No results found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {/* <SelectdDropDown
                  id=""
                  label="Report To"
                  required
                  placeholder="select users"
                  value={editingUser.reports_to_id || ""}
                  options={users.map((u) => ({
                    label: u.full_name,
                    value: u.id,
                  }))}
                  onChange={(value) =>
                    setEditingUser({ ...editingUser, reports_to_id: value })
                  }
                  // error={jobState.validateErrors.hiring_manager}
                /> */}

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    Update User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        onConfirm={confirmation.onConfirm}
        onClose={hideConfirmation}
        type={confirmation.type}
        loading={confirmation.loading}
      />

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
      {/* <ApprovalFlowView></ApprovalFlowView>
    <PoolNameView></PoolNameView> */}
    </div>
  );
};

export default UserManagement;
